import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import WistiaPlayer from "../components/WistiaPlayer";
import AnimatedNumber from "../components/AnimatedNumber";
import { WORKS } from "../data/content";
import { breadcrumbSchema } from "../lib/schemas";

const HERO_IMG = "/images/works/nike-jd/hero.jpg";
const GALLERY_01 = "/images/works/nike-jd/gallery-01.jpg";
const GALLERY_02 = "/images/works/nike-jd/gallery-02.jpg";

// Anti-duplication / missing-file placeholder.
// Renderizza un'immagine; se va in errore (404), mostra un placeholder
// editoriale OnSports con bordo lime tratteggiato e label "Foto in arrivo".
const SafeImage = ({ src, alt, className = "", aspectClass = "aspect-[4/3]" }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className={`relative ${aspectClass} bg-dodgers/5 border-2 border-dashed border-lime flex items-center justify-center ${className}`}
        role="img"
        aria-label={`${alt} — placeholder`}
      >
        <span className="font-condensed uppercase tracking-widest text-[12px] text-dodgers/60">
          Foto in arrivo
        </span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      className={`block w-full h-full object-cover ${className}`}
      loading="lazy"
    />
  );
};

const RelatedCard = ({ to, eyebrow, title, image }) => (
  <Link
    to={to}
    className="group block border border-mandrogni/30 hover:border-lime transition-colors duration-300 bg-galactico"
    data-hover="true"
  >
    <div className="aspect-[4/3] overflow-hidden bg-dodgers/5">
      <SafeImage src={image} alt={title} aspectClass="aspect-[4/3]" />
    </div>
    <div className="p-6">
      <p className="font-condensed uppercase tracking-widest text-[11px] text-lime mb-2">
        ● {eyebrow}
      </p>
      <h3
        className="font-display text-dodgers leading-[1.1] group-hover:text-lime transition-colors"
        style={{ fontSize: "clamp(20px, 2.4vw, 26px)" }}
      >
        {title}
      </h3>
    </div>
  </Link>
);

const WorkNikeJd = () => {
  const RELATED = [
    {
      to: "/works/kappa-x-coop",
      eyebrow: "Cultural Impact",
      title: "Kappa × Coop",
      image: "https://fast.wistia.com/embed/medias/148oeb8nf4/swatch",
    },
    {
      to: "/works/volleyball-world-italia",
      eyebrow: "Cultural Impact",
      title: "Volleyball World Italia",
      image: WORKS.find((w) => w.to === "/works/volleyball-world-italia")?.image,
    },
    {
      to: "/works/reale-mutua-essere-libero",
      eyebrow: "Cultural Impact",
      title: "Reale Mutua × Essere Libero",
      image: WORKS.find((w) => w.to === "/works/reale-mutua-essere-libero")
        ?.image,
    },
  ];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Nike × JD Sports", url: "/works/nike-jd" },
  ]);

  return (
    <>
      <Seo
        title="Nike × JD Sports: 623K impressions challenge TikTok | OnSports Case"
        description="Lancio Nike Vapormax in esclusiva JD Sports con challenge TikTok #NIKEVAPORMAXCHALLENGE e filtro 3D AR. 623K+ impressions, 34K+ interactions. OnSports 2022."
        path="/works/nike-jd"
        image="/images/works/nike-jd/hero.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO FULL-BLEED */}
      <section
        className="relative w-full overflow-hidden"
        style={{ minHeight: "78vh" }}
        data-testid="hero-nike-jd"
      >
        {/* Background image */}
        <div className="absolute inset-0">
          <SafeImage
            src={HERO_IMG}
            alt="Nike Vapormax — sneaker indossata, campagna JD Sports"
            aspectClass="absolute inset-0 w-full h-full"
            className="absolute inset-0 w-full h-full"
          />
        </div>
        {/* Overlay 30% */}
        <div
          className="absolute inset-0 bg-dodgers"
          style={{ opacity: 0.3 }}
          aria-hidden="true"
        />

        <div className="relative z-10 container-os pt-40 pb-16 md:pt-52 md:pb-24 min-h-[78vh] flex flex-col">
          <RevealOnScroll>
            <Link
              to="/works"
              className="inline-flex items-center gap-2 text-galactico/85 hover:text-lime transition-colors font-condensed uppercase tracking-widest text-sm"
              data-testid="back-to-works"
            >
              <ArrowLeft size={16} /> Tutti i casi studio
            </Link>
          </RevealOnScroll>

          <div className="mt-auto pt-12 md:pt-16">
            <RevealOnScroll delay={0.05}>
              <p className="font-condensed uppercase tracking-widest text-lime text-[13px]">
                ● Case study · Cultural Impact · Business Impact
              </p>
            </RevealOnScroll>
            <RevealOnScroll delay={0.12}>
              <h1
                className="font-display text-galactico mt-6 leading-[0.95]"
                style={{
                  fontSize: "clamp(40px, 7vw, 88px)",
                  letterSpacing: "-0.015em",
                }}
                data-testid="hero-title"
              >
                Nike × JD Sports
                <br />
                La Vapormax torna{" "}
                <span className="text-lime">VIRALE.</span>
              </h1>
            </RevealOnScroll>
            <RevealOnScroll delay={0.2}>
              <p
                className="mt-7 text-[18px] md:text-[20px] leading-[1.5] text-galactico/90 max-w-[760px]"
                style={{
                  fontFamily: "'Hanken Grotesk', sans-serif",
                  fontStyle: "italic",
                }}
              >
                Una challenge TikTok con filtro 3D AR per il lancio in
                esclusiva JD Sports. Sviluppata con TikTok Creative Lab,
                attivata da creator UK sport e lifestyle.
              </p>
            </RevealOnScroll>
            <RevealOnScroll delay={0.28}>
              <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 font-condensed uppercase tracking-widest text-[13px] text-galactico/85">
                <span>2022</span>
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
                <span>Cliente — Nike × JD Sports UK</span>
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
                <span>Partner — TikTok Creative Lab</span>
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-lime" aria-hidden="true" />
                <span>Format — #NIKEVAPORMAXCHALLENGE</span>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 2 — OVERVIEW */}
      <section className="bg-galactico text-dodgers py-16 md:py-20" data-testid="sec-overview">
        <div className="container-os">
          <div className="max-w-[760px] mx-auto">
            <RevealOnScroll>
              <SectionEyebrow>Overview</SectionEyebrow>
            </RevealOnScroll>
            <RevealOnScroll delay={0.06}>
              <h2
                className="font-display mt-5 leading-[1.05]"
                style={{ fontSize: "clamp(28px, 4vw, 44px)" }}
              >
                Un filtro 3D, dei creator giusti, una community sneaker accesa.
              </h2>
            </RevealOnScroll>
            <div
              className="mt-8 space-y-5 text-[17px] leading-[1.7]"
              style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
            >
              <RevealOnScroll delay={0.1}>
                <p>
                  Nike e JD Sports lanciano una collab in esclusiva nel Regno
                  Unito: il ritorno della Nike Vapormax sugli scaffali JD. Il
                  prodotto è iconico, ma riportarlo al centro della
                  conversazione richiede qualcosa di più di una campagna media
                  tradizionale. Serve far parlare la community, non solo parlare
                  alla community.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.16}>
                <p>
                  L'idea sviluppata da OnSports in collaborazione con il
                  Creative Lab di TikTok: un filtro 3D AR proprietario che
                  permette agli utenti di "provare" virtualmente la Vapormax
                  direttamente dal loro smartphone, prima di acquistarla.
                  Intorno al filtro si costruisce la #NIKEVAPORMAXCHALLENGE,
                  attivata da una selezione di creator inglesi del mondo sport
                  e lifestyle che fanno da innesco alla viralità.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 3 — LA SFIDA */}
      <section className="bg-dodgers text-galactico py-16 md:py-20" data-testid="sec-sfida">
        <div className="container-os">
          <div className="max-w-[760px] mx-auto">
            <RevealOnScroll>
              <SectionEyebrow>La sfida</SectionEyebrow>
            </RevealOnScroll>
            <RevealOnScroll delay={0.06}>
              <h2
                className="font-display mt-5 leading-[1.05]"
                style={{ fontSize: "clamp(28px, 4vw, 44px)" }}
              >
                Come si riporta in cima alla conversazione un prodotto già
                visto?
              </h2>
            </RevealOnScroll>
            <div
              className="mt-8 space-y-5 text-[17px] leading-[1.7] opacity-90"
              style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
            >
              <RevealOnScroll delay={0.1}>
                <p>
                  Il problema strategico era preciso. La Nike Vapormax aveva
                  già avuto la sua finestra di hype mainstream — non era una
                  sneaker nuova da introdurre, era una sneaker da rilanciare.
                  E il pubblico-target — la Gen Z UK appassionata di sneaker
                  culture, sport e lifestyle digitale — non risponde alle
                  campagne broadcast tradizionali. Risponde a contenuti che
                  sente come propri, prodotti dalla sua stessa community,
                  condivisi in linguaggi nativi delle piattaforme che usa ogni
                  giorno.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.16}>
                <p>
                  Per questo abbiamo scelto TikTok come perno della campagna.
                  Non come "canale aggiuntivo" rispetto al digital classico, ma
                  come spazio primario in cui far accadere il lancio. E abbiamo
                  costruito il prodotto creativo — il filtro 3D AR + la
                  challenge — direttamente dentro la grammatica della
                  piattaforma, in partnership con chi quella grammatica la
                  scrive ogni giorno: il Creative Lab di TikTok.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 4 — IL FORMAT */}
      <section className="bg-galactico text-dodgers py-16 md:py-20" data-testid="sec-format">
        <div className="container-os">
          <div className="max-w-[760px] mx-auto">
            <RevealOnScroll>
              <SectionEyebrow>Il format</SectionEyebrow>
            </RevealOnScroll>
            <RevealOnScroll delay={0.06}>
              <h2
                className="font-display mt-5 leading-[1.05]"
                style={{ fontSize: "clamp(28px, 4vw, 44px)" }}
              >
                #NIKEVAPORMAXCHALLENGE — il filtro AR diventa la challenge.
              </h2>
            </RevealOnScroll>
            <div
              className="mt-8 space-y-5 text-[17px] leading-[1.7]"
              style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
            >
              <RevealOnScroll delay={0.1}>
                <p>
                  Il fulcro del format è il filtro 3D AR sviluppato con TikTok
                  Creative Lab: una soluzione tecnologica che permette agli
                  utenti di indossare virtualmente la Nike Vapormax in tempo
                  reale, esplorarne i dettagli, condividere il risultato come
                  contenuto TikTok. La sneaker non viene mostrata, viene "messa
                  ai piedi" del pubblico ancora prima dell'acquisto.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.16}>
                <p>
                  Intorno al filtro si costruisce la challenge: i creator
                  selezionati pubblicano un contenuto utilizzando il filtro,
                  invitando la propria audience a partecipare. La community
                  organica replica il pattern. Il filtro diventa unità di
                  engagement; ogni utilizzo è una "ad" che il pubblico stesso
                  produce e distribuisce. Per Nike e JD Sports è esposizione
                  organica; per gli utenti è entertainment partecipato; per i
                  creator è contenuto nativo che non infrastruttura sponsorship
                  invasive.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 5 — VIDEO 1 (verticale 9:16) */}
      <section className="bg-dodgers py-12 md:py-16" data-testid="sec-video-1">
        <div className="container-os">
          <RevealOnScroll>
            <div className="max-w-[400px] mx-auto">
              <WistiaPlayer mediaId="e897tj16qh" aspect={9 / 16} />
            </div>
          </RevealOnScroll>
          <p
            className="mt-5 text-center text-mandrogni text-[14px]"
            style={{ fontFamily: "'Hanken Grotesk', sans-serif", fontStyle: "italic" }}
          >
            Uno dei contenuti dei creator UK partecipanti alla
            #NIKEVAPORMAXCHALLENGE
          </p>
        </div>
      </section>

      {/* SEZ. 6 — L'APPROCCIO CREATOR */}
      <section className="bg-galactico text-dodgers py-16 md:py-20" data-testid="sec-approccio">
        <div className="container-os">
          <div className="max-w-[760px] mx-auto">
            <RevealOnScroll>
              <SectionEyebrow>L'approccio creator</SectionEyebrow>
            </RevealOnScroll>
            <RevealOnScroll delay={0.06}>
              <h2
                className="font-display mt-5 leading-[1.05]"
                style={{ fontSize: "clamp(28px, 4vw, 44px)" }}
              >
                Non per follower count. Per autenticità rispetto al target.
              </h2>
            </RevealOnScroll>
            <div
              className="mt-8 space-y-5 text-[17px] leading-[1.7]"
              style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
            >
              <RevealOnScroll delay={0.1}>
                <p>
                  La selezione dei creator non ha seguito la logica più diffusa
                  nelle campagne brand — massimizzare il follower count totale.
                  Ha seguito una logica di matching specifico col target
                  Vapormax: sneakerheads UK, creator dello street style e della
                  cultura urban-sport, voci nella community sportiva digitale
                  britannica. Persone che non sembravano "ingaggiate" perché
                  stavano già parlando, nelle loro pubblicazioni regolari, di
                  sneaker culture, di lifestyle attivo, dei modelli iconici del
                  catalogo Nike.
                </p>
              </RevealOnScroll>
              <RevealOnScroll delay={0.16}>
                <p>
                  Il risultato è stato un volume di contenuti generati dalla
                  community che ha amplificato la portata della campagna ben
                  oltre le pubblicazioni dei creator stessi. La challenge ha
                  mantenuto rilevanza per settimane dopo il lancio, vivendo
                  della replicazione organica degli utenti TikTok che
                  continuavano ad utilizzare il filtro anche dopo la chiusura
                  della finestra di campagna paid.
                </p>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* SEZ. 7 — GALLERIA 2 IMG */}
      <section className="bg-dodgers py-12 md:py-16" data-testid="sec-gallery">
        <div className="container-os">
          <RevealOnScroll>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="aspect-[4/5] md:aspect-square overflow-hidden bg-galactico/5 border border-galactico/10">
                <SafeImage
                  src={GALLERY_01}
                  alt="Nike Vapormax — dettaglio sneaker bianca, lifestyle UK"
                  aspectClass="aspect-[4/5] md:aspect-square"
                />
              </div>
              <div className="aspect-[4/5] md:aspect-square overflow-hidden bg-galactico/5 border border-galactico/10">
                <SafeImage
                  src={GALLERY_02}
                  alt="Nike Vapormax — sneaker in azione su parete colorata, scena UK street"
                  aspectClass="aspect-[4/5] md:aspect-square"
                />
              </div>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 8 — VIDEO 2 (verticale 9:16) */}
      <section className="bg-galactico py-12 md:py-16" data-testid="sec-video-2">
        <div className="container-os">
          <RevealOnScroll>
            <div className="max-w-[400px] mx-auto">
              <WistiaPlayer mediaId="5bvb2blnqu" aspect={9 / 16} />
            </div>
          </RevealOnScroll>
          <p
            className="mt-5 text-center text-mandrogni text-[14px]"
            style={{ fontFamily: "'Hanken Grotesk', sans-serif", fontStyle: "italic" }}
          >
            Il filtro 3D AR in azione: la Vapormax indossabile direttamente da
            smartphone
          </p>
        </div>
      </section>

      {/* SEZ. 9 — L'IMPATTO (KPI giganti) */}
      <section
        className="bg-dodgers text-galactico py-24 md:py-32"
        data-testid="sec-impatto"
      >
        <div className="container-os">
          <div className="text-center max-w-[820px] mx-auto">
            <RevealOnScroll>
              <SectionEyebrow>L'impatto</SectionEyebrow>
            </RevealOnScroll>
            <RevealOnScroll delay={0.06}>
              <h2
                className="font-display mt-5 leading-[1.05]"
                style={{ fontSize: "clamp(28px, 4vw, 44px)" }}
              >
                Una sneaker già vista. Riportata al{" "}
                <span className="text-lime">CENTRO</span>.
              </h2>
            </RevealOnScroll>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 mt-16 md:mt-20 max-w-[1100px] mx-auto">
            <RevealOnScroll delay={0.1}>
              <div className="text-center md:text-left">
                <AnimatedNumber
                  end={623}
                  suffix="K+"
                  className="font-display text-lime block leading-[0.9] kpi-giant"
                />
                <p className="mt-4 font-condensed uppercase tracking-widest text-[14px] text-galactico/80">
                  Impressions cumulate
                </p>
              </div>
            </RevealOnScroll>
            <RevealOnScroll delay={0.18}>
              <div className="text-center md:text-left">
                <AnimatedNumber
                  end={34}
                  suffix="K+"
                  className="font-display text-lime block leading-[0.9] kpi-giant"
                />
                <p className="mt-4 font-condensed uppercase tracking-widest text-[14px] text-galactico/80">
                  Interazioni dirette
                </p>
              </div>
            </RevealOnScroll>
          </div>
          <style>{`.kpi-giant { font-size: clamp(72px, 11vw, 140px); letter-spacing: -0.02em; }`}</style>

          <RevealOnScroll delay={0.25}>
            <p
              className="mt-16 text-center text-[17px] leading-[1.7] opacity-90 max-w-[720px] mx-auto"
              style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
            >
              Una campagna che ha trasformato il rilancio di un prodotto già
              nel catalogo Nike in un momento culturale per la sneaker
              community UK. Il filtro 3D AR ha continuato ad essere utilizzato
              anche oltre la finestra di campagna, generando esposizione
              organica residuale che non era stata pagata.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 10 — CREDITS */}
      <section className="bg-galactico text-dodgers py-12 md:py-16" data-testid="sec-credits">
        <div className="container-os">
          <div className="max-w-[720px] mx-auto">
            <RevealOnScroll>
              <SectionEyebrow>Credits</SectionEyebrow>
            </RevealOnScroll>
            <RevealOnScroll delay={0.06}>
              <h2
                className="font-display mt-5 leading-[1.1]"
                style={{ fontSize: "clamp(24px, 3vw, 36px)" }}
              >
                Chi ha fatto cosa.
              </h2>
            </RevealOnScroll>

            <RevealOnScroll delay={0.12}>
              <dl className="mt-8 border-t border-dodgers/15">
                {[
                  ["Project Lead & Strategy", "OnSports"],
                  ["Creative Lab & filtro 3D AR", "TikTok Creative Lab"],
                  ["Creator management", "OnSports"],
                  ["Cliente", "Nike × JD Sports UK"],
                  ["Anno", "2022"],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="grid grid-cols-12 gap-3 py-4 border-b border-dodgers/15 text-[15px] leading-[1.55]"
                  >
                    <dt className="col-span-5 md:col-span-4 font-condensed uppercase tracking-widest text-[12px] md:text-[13px] opacity-70">
                      {label}
                    </dt>
                    <dd className="col-span-7 md:col-span-8 font-medium">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 11 — CTA FINALE */}
      <section
        className="bg-dodgers text-galactico py-20 md:py-28"
        data-testid="sec-cta-finale"
      >
        <div className="container-os">
          <div className="max-w-[760px]">
            <RevealOnScroll>
              <h2
                className="font-display leading-[1.05]"
                style={{ fontSize: "clamp(28px, 4vw, 44px)" }}
              >
                Hai un prodotto da rilanciare via community digitale?
                <br />
                <span className="text-lime">PARLIAMONE</span>.
              </h2>
            </RevealOnScroll>
            <RevealOnScroll delay={0.1}>
              <p
                className="mt-7 text-[16px] md:text-[17px] leading-[1.65] opacity-90 max-w-[600px]"
                style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}
              >
                Una call iniziale di 30 minuti per capire se la community
                giusta per il tuo prodotto esiste già — o se va attivata da
                zero.
              </p>
            </RevealOnScroll>
            <RevealOnScroll delay={0.18}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contatti" data-testid="cta-parliamone">
                  <Button variant="primary">Parliamone →</Button>
                </Link>
                <Link to="/works" data-testid="cta-altri-case">
                  <Button variant="outline">
                    Vedi altri case Cultural Impact →
                  </Button>
                </Link>
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </section>

      {/* SEZ. 12 — NAVIGATION CORRELATI */}
      <section
        className="bg-galactico text-dodgers py-12 md:py-16 border-t border-dodgers/10"
        data-testid="sec-related"
      >
        <div className="container-os">
          <RevealOnScroll>
            <SectionEyebrow>Continua a esplorare</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-4 mb-10">Case correlati.</h2>
          </RevealOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {RELATED.map((r) => (
              <RevealOnScroll key={r.to} delay={0.1}>
                <RelatedCard
                  to={r.to}
                  eyebrow={r.eyebrow}
                  title={r.title}
                  image={r.image}
                />
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkNikeJd;
